import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Calendar, MapPin, Radio, Tv, Quote, Sparkles, Scale,
  Newspaper, Megaphone, Pin, Star, Play, Image as ImageIcon, Clock, Eye,
} from "lucide-react";
import api from "../lib/api";
import Logo from "../components/Logo";

const PRESS_IMG = "https://customer-assets.emergentagent.com/job_frontuni-rdc/artifacts/76o7ycxx_IMG_2667.JPG";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_frontuni-rdc/artifacts/wjb8wwvi_IMG_1479.png";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };
const fmtShort = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return d; } };

export default function HomePage() {
  const [hero, setHero] = useState({ featured: [], pinned: [], latest: [] });
  const [communiques, setCommuniques] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    api.get("/news/hero").then((r) => setHero({
      featured: r.data.featured || [],
      pinned: r.data.pinned || [],
      latest: r.data.latest || [],
    })).catch(() => {});
    api.get("/news?category=Communiqu%C3%A9s%20officiels&limit=3").then((r1) => {
      // Combine avec "Communiqués de presse"
      api.get("/news?category=Communiqu%C3%A9s%20de%20presse&limit=3").then((r2) => {
        const combined = [...(r1.data || []), ...(r2.data || [])]
          .sort((a, b) => (b.published_at || "").localeCompare(a.published_at || ""))
          .slice(0, 3);
        setCommuniques(combined);
      }).catch(() => setCommuniques(r1.data || []));
    }).catch(() => {});
    api.get("/events/upcoming?limit=3").then((r) => setUpcoming(r.data)).catch(() => {});
    api.get("/events/past?limit=3").then((r) => setPast(r.data)).catch(() => {});
    api.get("/media?type=photo").then((r) => setMedia((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  // Side items = pinned first, complétés par latest si nécessaire
  const sideItems = [...(hero.pinned || []), ...(hero.latest || [])].slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* ===== HERO ACTUALITÉS : Carrousel de "à la une" + mini cartes épinglées ===== */}
      {hero.featured.length > 0 ? (
        <HeroNews featuredList={hero.featured} sideItems={sideItems} />
      ) : (
        <GenericHero />
      )}

      {/* ===== VISION ===== */}
      <VisionSection />

      {/* ===== DERNIÈRES ACTUALITÉS (grille) ===== */}
      <LatestNewsSection featured={hero.featured[0]} latest={sideItems} />

      {/* ===== ÉVÉNEMENTS À VENIR ===== */}
      <EventsSection title="Prochains rendez-vous" eyebrow="Mobilisation" items={upcoming} empty="Aucun événement à venir pour le moment." testid="home-upcoming" />

      {/* ===== ÉVÉNEMENTS PASSÉS ===== */}
      <PastEventsSection items={past} />

      {/* ===== COMMUNIQUÉS OFFICIELS ===== */}
      {communiques.length > 0 && <CommuniquesSection items={communiques} />}

      {/* ===== WEB TV & RADIO ===== */}
      <MediaShortcuts media={media} />
    </div>
  );
}

/* ============ HERO ACTUALITÉS — Carrousel auto-rotatif ============ */
function HeroNews({ featuredList, sideItems }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = featuredList.length;

  // Auto-rotation toutes les 4 secondes
  useEffect(() => {
    if (paused || total <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx((i) => (i + 1) % total);
    }, 4000);
    return () => clearInterval(timer);
  }, [paused, total]);

  const goTo = (i) => { setActiveIdx(i); };
  const next = () => setActiveIdx((i) => (i + 1) % total);
  const prev = () => setActiveIdx((i) => (i - 1 + total) % total);

  const featured = featuredList[activeIdx];

  return (
    <section className="bg-ink text-white relative overflow-hidden grain" data-testid="home-hero-news">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(212,175,55,0.14),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_80%,rgba(200,16,46,0.10),transparent_55%)]" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-16 lg:pt-16 lg:pb-24">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="eyebrow eyebrow-on-dark flex items-center gap-3">
              <span className="w-8 h-px bg-gold" />
              <Newspaper className="w-3.5 h-3.5" /> À la une
              {total > 1 && <span className="ml-2 text-white/40">· {total} publications</span>}
            </div>
            <p className="text-white/70 text-sm mt-2 max-w-xl">Retrouvez immédiatement les actualités principales du Front Uni pour la RDC.</p>
          </div>
          <Link to="/actualites" className="text-gold hover:text-white text-sm font-semibold inline-flex items-center gap-2 border-b border-gold pb-1" data-testid="home-see-all-news">
            Toutes les actualités <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Grande vedette CAROUSEL (gauche) */}
          <div className="lg:col-span-8 relative"
               onMouseEnter={() => setPaused(true)}
               onMouseLeave={() => setPaused(false)}
               data-testid="hero-featured-carousel">
            {/* Slides — crossfade */}
            <div className="relative aspect-[16/10] lg:aspect-[16/11] rounded-sm overflow-hidden bg-ink">
              {featuredList.map((n, i) => (
                <Link
                  key={n.id}
                  to={`/actualites/${n.slug}`}
                  className={`absolute inset-0 group transition-opacity duration-1000 ease-in-out ${i === activeIdx ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
                  data-testid={`hero-slide-${i}`}
                  data-active={i === activeIdx}
                >
                  <div className="absolute inset-0">
                    {n.cover_image ? (
                      <img src={n.cover_image} alt={n.title}
                           className={`w-full h-full object-cover ${i === activeIdx ? "animate-ken-burns-slow" : ""}`} />
                    ) : (
                      <div className="w-full h-full bg-ink grid place-items-center">
                        <Newspaper className="w-20 h-20 text-gold/30" />
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 lg:p-10">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider bg-gold text-ink px-2 py-1 rounded font-bold">
                        <Star className="w-3 h-3" /> À la une
                      </span>
                      <span className="eyebrow eyebrow-on-dark">{n.category} · {fmt(n.published_at)}</span>
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                      {n.title}
                    </h1>
                    <p className="mt-4 text-white/85 text-base lg:text-lg leading-relaxed max-w-2xl line-clamp-2">
                      {n.excerpt}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-gold font-semibold text-sm group-hover:gap-4 transition-all">
                      Lire l'article <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}

              {/* Contrôles carrousel */}
              {total > 1 && (
                <>
                  <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-ink/70 backdrop-blur border border-white/20 hover:border-gold text-white hover:text-gold grid place-items-center transition-all" data-testid="hero-prev" aria-label="Précédent">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-ink/70 backdrop-blur border border-white/20 hover:border-gold text-white hover:text-gold grid place-items-center transition-all" data-testid="hero-next" aria-label="Suivant">
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              )}

              {/* Dots */}
              {total > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
                  {featuredList.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className={`transition-all ${i === activeIdx ? "w-8 h-1.5 bg-gold" : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"} rounded-full`}
                      data-testid={`hero-dot-${i}`}
                      aria-label={`Aller à la vedette ${i + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Timeline progress bar */}
              {total > 1 && !paused && (
                <div className="absolute top-0 left-0 h-0.5 bg-gold/80 z-20 animate-hero-progress" key={activeIdx} />
              )}
            </div>
          </div>

          {/* Mini cartes ÉPINGLÉES (droite) — restent fixes */}
          <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4" data-testid="hero-side-pinned">
            {sideItems.map((n) => (
              <Link key={n.id} to={`/actualites/${n.slug}`}
                    className="group flex gap-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/40 rounded-sm p-3.5 transition-all backdrop-blur-sm"
                    data-testid={`hero-side-news-${n.slug}`}>
                <div className="w-24 h-24 shrink-0 overflow-hidden rounded-sm bg-ink">
                  {n.cover_image && <img src={n.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="eyebrow eyebrow-on-dark text-[0.6rem] flex items-center gap-1.5">
                    {n.is_pinned && <Pin className="w-2.5 h-2.5" />} {n.category}
                  </div>
                  <h3 className="font-serif text-base lg:text-lg text-white leading-tight mt-1.5 line-clamp-3">{n.title}</h3>
                  <div className="mt-1.5 text-[0.68rem] text-white/50 flex items-center gap-1.5">
                    <Clock className="w-2.5 h-2.5" /> {fmt(n.published_at)}
                  </div>
                </div>
              </Link>
            ))}
            {sideItems.length === 0 && (
              <div className="text-white/50 text-sm border border-dashed border-white/15 rounded-sm p-6 text-center">
                Aucune autre actualité disponible.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ VISION ============ */
function VisionSection() {
  return (
    <section className="bg-white py-20 lg:py-28 relative overflow-hidden" data-testid="home-vision">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-ink/5 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="aspect-[4/3] overflow-hidden rounded-sm shadow-2xl">
            <img src={PRESS_IMG} alt="Conférence de presse — Front Uni pour la RDC" className="w-full h-full object-cover" />
          </div>
          <div className="eyebrow text-ink/60 mt-4 text-center">Conférence de presse · RTNC · Bruxelles</div>
        </div>
        <div className="lg:col-span-7">
          <div className="eyebrow">Notre vision</div>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mt-4 leading-tight text-ink">
            Plaidoyer européen.<br /><span className="text-foreground italic font-light">Solidarité concrète.</span>
          </h2>
          <div className="w-16 h-1 bg-gold mt-6" />
          <p className="mt-6 text-foreground/75 leading-relaxed max-w-xl text-[1.02rem]">
            Le Front Uni pour la RDC réunit la diaspora congolaise autour de quatre priorités :
            protection effective des civils, accès humanitaire sans entrave, justice et lutte contre l'impunité,
            traçabilité des chaînes d'approvisionnement.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-6 max-w-2xl">
            {[
              { k: "20+", v: "Actions de plaidoyer européennes" },
              { k: "3", v: "Continents mobilisés" },
              { k: "30 ans", v: "De crise dont nous portons la voix" },
            ].map((s) => (
              <div key={s.k} className="border-l-2 border-gold pl-4">
                <div className="font-serif text-3xl text-ink">{s.k}</div>
                <div className="text-foreground/65 text-sm mt-1">{s.v}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/adhesion" className="btn-gold" data-testid="home-vision-adhesion">
              Devenir membre <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/organisation" className="btn-outline" data-testid="home-vision-cta">
              Découvrir l'organisation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ DERNIÈRES ACTUALITÉS (grille) ============ */
function LatestNewsSection({ featured, latest }) {
  const excludeIds = new Set([featured?.id, ...latest.map((n) => n.id)].filter(Boolean));
  const [more, setMore] = useState([]);
  useEffect(() => {
    api.get("/news?limit=10").then((r) => {
      setMore((r.data || []).filter((n) => !excludeIds.has(n.id)).slice(0, 4));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured?.id]);

  if (more.length === 0) return null;
  return (
    <section className="bg-bone py-20 lg:py-24" data-testid="home-latest">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow text-gold-dark">Dernières publications</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink tracking-tight mt-3">Actualités récentes</h2>
          </div>
          <Link to="/actualites" className="btn-outline text-sm" data-testid="home-latest-more">
            Voir plus <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {more.map((n) => (
            <Link key={n.id} to={`/actualites/${n.slug}`}
                  className="group block bg-white border border-border rounded-sm overflow-hidden hover:border-ink/30 hover:shadow-md transition-all"
                  data-testid={`home-latest-${n.slug}`}>
              <div className="aspect-[16/10] overflow-hidden bg-ink/5">
                {n.cover_image
                  ? <img src={n.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  : <div className="w-full h-full grid place-items-center text-foreground/25"><Newspaper className="w-8 h-8" /></div>
                }
              </div>
              <div className="p-4">
                <div className="eyebrow text-gold-dark text-[0.62rem] flex items-center gap-1.5">
                  {n.is_pinned && <Pin className="w-2.5 h-2.5" />}
                  {n.is_featured && <Star className="w-2.5 h-2.5 text-gold-dark" />}
                  {n.category}
                </div>
                <h3 className="font-serif text-lg text-ink leading-snug mt-2 line-clamp-3">{n.title}</h3>
                <div className="mt-3 flex items-center justify-between text-[0.7rem] text-foreground/50">
                  <span>{fmt(n.published_at)}</span>
                  <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {n.view_count || 0}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ ÉVÉNEMENTS À VENIR ============ */
function EventsSection({ title, eyebrow, items, empty, testid }) {
  return (
    <section className="bg-white py-20 lg:py-24 border-t border-border" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow text-gold-dark">{eyebrow}</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink tracking-tight mt-3">{title}</h2>
          </div>
          <Link to="/evenements" className="btn-outline text-sm" data-testid={`${testid}-all`}>
            Tous les événements <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="text-foreground/60">{empty}</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {items.map((ev) => (
              <Link key={ev.id} to={`/evenements/${ev.slug}`}
                    className="group bg-bone border border-border rounded-sm p-6 transition-all hover:border-ink/30 hover:shadow-md hover:-translate-y-0.5"
                    data-testid={`${testid}-item-${ev.slug}`}>
                <div className="eyebrow text-gold-dark">{fmt(ev.date)} {ev.time && `· ${ev.time}`}</div>
                <h3 className="font-serif text-2xl text-ink mt-3 leading-tight">{ev.title}</h3>
                <p className="mt-2 text-foreground/65 text-sm line-clamp-2">{ev.theme}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-foreground/70">
                  <MapPin className="w-4 h-4 text-gold-dark" />
                  {ev.hide_location ? "Lieu communiqué après inscription" : `${ev.city}, ${ev.country}`}
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-ink font-semibold text-sm group-hover:gap-3 transition-all">
                  Voir le programme <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ============ ÉVÉNEMENTS PASSÉS ============ */
function PastEventsSection({ items }) {
  if (items.length === 0) return null;
  return (
    <section className="bg-ink text-white py-20 lg:py-24" data-testid="home-past-events">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow eyebrow-on-dark">Mémoire vivante</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mt-3">Événements passés</h2>
            <p className="text-white/60 text-sm mt-2 max-w-xl">Retour sur nos mobilisations et conférences déjà réalisées.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((ev) => (
            <Link key={ev.id} to={`/evenements/${ev.slug}`}
                  className="group bg-white/5 hover:bg-white/10 border border-white/15 hover:border-gold/40 rounded-sm overflow-hidden backdrop-blur-sm transition-all"
                  data-testid={`home-past-${ev.slug}`}>
              <div className="aspect-[16/10] overflow-hidden bg-ink">
                {ev.images?.[0] ? (
                  <img src={ev.images[0]} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-gold/30"><Calendar className="w-10 h-10" /></div>
                )}
              </div>
              <div className="p-5">
                <div className="eyebrow eyebrow-on-dark text-[0.62rem]">{fmt(ev.date)} · {ev.city}</div>
                <h3 className="font-serif text-xl text-white leading-tight mt-2 line-clamp-2">{ev.title}</h3>
                <div className="mt-3 inline-flex items-center gap-1.5 text-gold text-sm font-semibold group-hover:gap-3 transition-all">
                  Revoir <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ COMMUNIQUÉS ============ */
function CommuniquesSection({ items }) {
  return (
    <section className="bg-crimson/5 py-20 lg:py-24 border-y border-crimson/15" data-testid="home-communiques">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow text-crimson flex items-center gap-2"><Megaphone className="w-3.5 h-3.5" /> Officiel</div>
            <h2 className="font-serif text-4xl lg:text-5xl text-ink tracking-tight mt-3">Communiqués officiels</h2>
          </div>
          <Link to="/actualites" className="btn-outline text-sm" data-testid="home-communiques-all">
            Voir tous <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((n) => (
            <Link key={n.id} to={`/actualites/${n.slug}`}
                  className="group bg-white border-l-4 border-crimson rounded-sm p-6 hover:shadow-lg transition-all"
                  data-testid={`home-communique-${n.slug}`}>
              <div className="eyebrow text-crimson text-[0.65rem]">Communiqué · {fmt(n.published_at)}</div>
              <h3 className="font-serif text-xl text-ink mt-3 leading-tight line-clamp-3">{n.title}</h3>
              <p className="mt-3 text-foreground/70 text-sm line-clamp-3">{n.excerpt}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-crimson font-semibold text-sm group-hover:gap-3 transition-all">
                Lire le communiqué <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ MEDIA / WEBTV / RADIO ============ */
function MediaShortcuts({ media }) {
  return (
    <section className="py-20 bg-white border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Web TV */}
          <Link to="/web-tv" className="lg:col-span-5 group bg-ink text-white p-8 lg:p-10 rounded-sm relative overflow-hidden" data-testid="home-webtv">
            <Tv className="w-10 h-10 text-gold" />
            <h3 className="font-serif text-3xl mt-6 leading-tight">Web TV</h3>
            <p className="mt-2 text-white/70 max-w-md">Lives, replays, conférences et débats en catégories.</p>
            <div className="mt-8 inline-flex items-center gap-2 text-gold font-semibold text-sm group-hover:gap-4 transition-all">
              Regarder <ArrowRight className="w-4 h-4" />
            </div>
            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
          </Link>

          {/* Radio */}
          <Link to="/radio" className="lg:col-span-3 group bg-white border-2 border-ink p-8 lg:p-10 rounded-sm relative" data-testid="home-radio">
            <Radio className="w-10 h-10 text-crimson" />
            <h3 className="font-serif text-3xl mt-6 text-ink leading-tight">Radio</h3>
            <p className="mt-2 text-foreground/65 max-w-md">Direct audio, podcasts et débats.</p>
            <div className="mt-8 inline-flex items-center gap-2 text-ink font-semibold text-sm group-hover:gap-4 transition-all">
              Écouter <ArrowRight className="w-4 h-4" />
            </div>
          </Link>

          {/* Médias */}
          <Link to="/medias" className="lg:col-span-4 group bg-bone border border-border p-8 lg:p-10 rounded-sm relative" data-testid="home-medias">
            <ImageIcon className="w-10 h-10 text-gold-dark" />
            <h3 className="font-serif text-3xl mt-6 text-ink leading-tight">Galerie médias</h3>
            <p className="mt-2 text-foreground/65 max-w-md">Photos, vidéos, interviews et archives.</p>
            {media.length > 0 && (
              <div className="mt-5 grid grid-cols-4 gap-1.5">
                {media.slice(0, 4).map((m) => (
                  <div key={m.id} className="aspect-square rounded-sm overflow-hidden bg-ink/5">
                    <img src={m.thumbnail || m.url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 inline-flex items-center gap-2 text-ink font-semibold text-sm group-hover:gap-4 transition-all">
              Ouvrir la galerie <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============ Fallback Hero si aucune news ============ */
function GenericHero() {
  return (
    <section className="relative bg-ink text-white overflow-hidden grain">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <div className="eyebrow eyebrow-on-dark flex items-center gap-3">
              <span className="w-8 h-px bg-gold" /> Diaspora congolaise
            </div>
            <h1 className="font-serif mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-white">
              Une voix unie<br /><span className="text-gold italic font-light">pour la République démocratique du Congo.</span>
            </h1>
            <p className="mt-8 text-white/80 max-w-2xl text-lg leading-relaxed">
              Plateforme institutionnelle de la diaspora congolaise.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/evenements" className="btn-gold">Nos prochains rendez-vous <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/actualites" className="text-white/90 hover:text-gold inline-flex items-center gap-2 font-semibold text-sm tracking-wide">
                Lire nos actualités <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="border-l border-gold/50 pl-6 animate-slide-in-right">
              <Quote className="w-6 h-6 text-gold" />
              <p className="mt-4 font-serif text-xl text-white/90 leading-snug italic">
                « Au-delà des États, la société civile congolaise et sa diaspora peuvent peser dans les espaces institutionnels internationaux. »
              </p>
              <div className="mt-4 eyebrow eyebrow-on-dark">— Dr Audrey Mundine Kabeya, Présidente</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
